import React from 'react';
import { gradientForName } from '../lib/gradientForName';
import './PlaylistCover.css';

/**
 * PlaylistCover — renders a playlist cover image with AVIF/WebP/JPG sources.
 * Falls back to a deterministic gradient when no cover image is available.
 *
 * @param {object} props
 * @param {string|null} props.cover - cover identifier (e.g. "morning-coffee") or null
 * @param {string} props.name - playlist name (used for gradient fallback)
 * @param {string} [props.icon] - playlist emoji icon (shown over gradient)
 * @param {number} [props.size=40] - pixel size (width and height)
 * @param {string} [props.className]
 */
function PlaylistCover({
  cover,
  name,
  icon,
  size = 40,
  className = '',
  onClick,
  style,
}) {
  const hasCover = !!cover;
  const gradient = gradientForName(name);
  const isAbsoluteUrl = hasCover && /^(https?:|\/|data:|blob:)/.test(cover);

  if (hasCover) {
    // Absolute URL (Unsplash, R2, blob, etc.) → use as-is
    if (isAbsoluteUrl) {
      return (
        <img
          src={cover}
          alt={name}
          width={size}
          height={size}
          loading="lazy"
          decoding="async"
          onClick={onClick}
          className={`playlist-cover playlist-cover--image ${className}`}
          style={{ width: size, height: size, ...style }}
        />
      );
    }
    // Relative cover ID (e.g. "morning-coffee") → use Sharp pipeline output
    return (
      <picture
        className={`playlist-cover playlist-cover--image ${className}`}
        style={{ width: size, height: size, ...style }}
        onClick={onClick}
      >
        <source srcSet={`/covers/${cover}.avif`} type="image/avif" />
        <source srcSet={`/covers/${cover}.webp`} type="image/webp" />
        <img
          src={`/covers/${cover}.jpg`}
          alt={name}
          width={size}
          height={size}
          loading="lazy"
          decoding="async"
          className="playlist-cover__img"
        />
      </picture>
    );
  }

  return (
    <div
      className={`playlist-cover playlist-cover--gradient ${className}`}
      style={{ width: size, height: size, background: gradient, ...style }}
      onClick={onClick}
      aria-hidden="true"
    >
      {icon && <span className="playlist-cover__icon">{icon}</span>}
    </div>
  );
}

export default PlaylistCover;
